import { Fragment, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Link from 'next/link';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Tabs from '../tabs';
import MaterialLinkButton from '../material-link-button';
import EventHeaderTags from './header-tags';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0),
    [theme.breakpoints.down('sm')]: {
      flexFlow: 'column',
      alignItems: 'flex-start',
    },
  },
  headerLeft: {
    flexGrow: 1,
    paddingRight: theme.spacing(2),
  },
  title: {
    margin: 0,
  },
  subtitle: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    '& > *': {
      '&:not(:last-child)': {
        marginRight: theme.spacing(0.5),
      },
    },
  },
  iconChip: {
    maxWidth: '100%',
    '& > span': {
      display: 'inline-flex',
      alignItems: 'center',
    },
  },
  icon: {
    marginRight: theme.spacing(0.5),
    fontSize: 16,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexFlow: 'column',
      alignItems: 'flex-start',
      maxWidth: '100%',
      width: '100%',
    },
  },
  publishAlert: {
    padding: theme.spacing(1.5, 1.5, 1.5, 2.5),
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
    border: `2px solid ${theme.palette.secondary.main}`,
  },
  publishMessage: {
    marginRight: theme.spacing(3),
  },
  tabs: {
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2),
    },
  },
  titleLink: {
    color: 'rgba(0, 0, 0, 0.87)',
    textDecoration: 'none',
  },
}));

const EventHeader = (props) => {
  const { event, tab, noTabs } = props;
  const classes = useStyles();

  const handleChangeTab = useCallback((e, newTab) => {
    if (newTab === 'overview') {
      e.preventDefault();
      window.scrollTo(0, 0);
    }
    // else if (newTab === 'discussion') {
    //   document.getElementById('discussion').scrollIntoView();
    // }
  }, [event]);

  const tabOptions = useMemo(() => {
    if (noTabs) {
      return [];
    }

    let options = [
      {
        label: 'Overview',
        value: 'overview',
      },
    ];
    if (event.permissions.approved) {
      options.push({
        label: `Discussion (${event.imageUploadsCount + event.commentCount})`,
        value: 'discussion',
        component: 'a',
        href: '#discussion'
      });
    }
    return options;
  }, [event, noTabs]);

  return (
    <Fragment>
      {event.status === 'DRAFT' && event.permissions.editable && (
        <Paper className={classes.publishAlert}>
          <div className={classes.publishMessage}>
            This event is currently unpublished
          </div>
          <MaterialLinkButton
            variant="outlined"
            href="/events/[id]/edit"
            as={`/events/${event.id}/edit`}
            className={classes.publishButton}
          >
            Edit to Publish
          </MaterialLinkButton>
        </Paper>
      )}

      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <Typography variant="h1" className={classes.title}>
            <Link
              href="/events/[id]"
              as={`/events/${event.id}`}
              className={classes.titleLink}
            >
              {event.name}
            </Link>
          </Typography>

          <div className={classes.subtitle}>
            <EventHeaderTags event={event} showVisibility />
          </div>
        </div>

        <div className={classes.headerRight}>
          {tabOptions.length > 1 && (
            <Tabs
              options={tabOptions}
              tab={tab}
              className={classes.tabs}
              onTabChange={handleChangeTab}
            />
          )}
        </div>
      </div>
    </Fragment>
  );
}

EventHeader.propTypes = {
  event: PropTypes.object,
  tab: PropTypes.string,
  noTabs: PropTypes.bool,
};

EventHeader.defaultProps = {
  noTabs: false,
};

export default EventHeader;
